import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL
const key = import.meta.env.VITE_SUPABASE_ANON_KEY

// Forward ref — fetchWithTimeout references `supabase` inside its body, but only
// at fetch-time. By the time any fetch actually runs, createClient has returned
// and supabase is assigned, so the reference resolves.
let supabase = null

// Raw fetch with a 25s safety-net timeout for non-storage requests. Storage has
// its own hard deadline (10 min in uploadAttachment). 25s sits ABOVE the
// writeQueue's 20s deadline (writeQueue.js WRITE_TIMEOUT_MS) so that the queue's
// `Promise.race` is always the one that fires first under contention — giving
// callers a clean "Write timed out" error with retry semantics, instead of an
// AbortError that races ahead of the queue and short-circuits its 5s auth-lock
// budget. The previous 12s ceiling silently invalidated the queue's 20s budget,
// causing every write to timeout once auth-lock contention or slow networks
// pushed real fetch duration past 12s.
const RAW_FETCH_TIMEOUT_MS = 25000

// Ring buffer of recent fetch durations, exposed for the sync diagnostic panel.
// Cap at 50 entries; keep oldest-first so consumers can compute avg/p99 cheaply.
const RECENT_FETCHES_MAX = 50
const _recentFetches = []
function _recordFetch(entry) {
  _recentFetches.push(entry)
  if (_recentFetches.length > RECENT_FETCHES_MAX) _recentFetches.shift()
}
export function getRecentFetches() { return _recentFetches.slice() }

// Project URL + anon key, re-exported so the lock-free recovery path can build raw
// PostgREST requests without reaching through the supabase-js client (whose auth lock
// is exactly what wedges under the in-tab GoTrueClient deadlock).
export const SUPABASE_URL = url
export const SUPABASE_ANON_KEY = key

/**
 * Lock-free DB reachability probe. Hits PostgREST directly with a plain fetch() and the
 * caller's CACHED bearer token — it never touches the supabase-js client, so it bypasses
 * the gotrue auth lock entirely. This is the disambiguator: if this succeeds while the
 * client-routed pingHealth() hangs, the fault is the in-tab auth-lock wedge, not the network.
 * Returns { ok, status, latencyMs, error }.
 */
export async function rawRestProbe({ token, userId, timeoutMs = 5000 }) {
  if (!url || !key) return { ok: false, error: 'No client config' }
  if (!token || !userId) return { ok: false, error: 'No cached token' }
  const started = performance.now()
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(`${url}/rest/v1/profiles?select=id&id=eq.${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers: { apikey: key, Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
    const latencyMs = Math.round(performance.now() - started)
    return { ok: res.ok, status: res.status, latencyMs, error: res.ok ? null : `HTTP ${res.status}` }
  } catch (err) {
    const latencyMs = Math.round(performance.now() - started)
    return { ok: false, latencyMs, error: err?.message || 'probe failed' }
  } finally {
    clearTimeout(timer)
  }
}

const rawFetch = (input, init) => {
  const reqUrl = typeof input === 'string' ? input : (input?.url || '')
  if (reqUrl.includes('/storage/v1/')) return fetch(input, init)

  const controller = new AbortController()
  let abortedByTimer = false
  const timer = setTimeout(() => { abortedByTimer = true; controller.abort() }, RAW_FETCH_TIMEOUT_MS)
  const callerSignal = init?.signal
  if (callerSignal) {
    callerSignal.addEventListener('abort', () => controller.abort(), { once: true })
  }
  const started = performance.now()
  return fetch(input, { ...init, signal: controller.signal })
    .then((res) => {
      _recordFetch({ url: reqUrl, ms: Math.round(performance.now() - started), ok: true, status: res.status, at: Date.now() })
      return res
    })
    .catch((err) => {
      const ms = Math.round(performance.now() - started)
      const reason = abortedByTimer ? `safety-net (${RAW_FETCH_TIMEOUT_MS}ms)` : 'caller-signal'
      _recordFetch({ url: reqUrl, ms, ok: false, aborted: true, reason, at: Date.now() })
      if (abortedByTimer) console.warn(`[rawFetch] aborted by ${reason} after ${ms}ms — ${reqUrl}`)
      throw err
    })
    .finally(() => clearTimeout(timer))
}

// Auto-recover from expired-JWT 401s: refresh the session once and retry the
// original request with the new token. Skips auth endpoints (the auth client
// handles those itself) and storage endpoints (uploadAttachment has its own
// cached-token fast path). This removes the "feature quietly stops working
// after 15 min because my tab was backgrounded" class of bugs.
const fetchWithTimeout = async (input, init) => {
  const reqUrl = typeof input === 'string' ? input : (input?.url || '')
  const res = await rawFetch(input, init)

  if (
    res.status !== 401 ||
    !supabase ||
    reqUrl.includes('/auth/v1/') ||
    reqUrl.includes('/storage/v1/')
  ) return res

  try {
    const refreshed = await Promise.race([
      supabase.auth.refreshSession(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('refresh timeout')), 6000)),
    ])
    const newToken = refreshed?.data?.session?.access_token
    if (!newToken) return res
    const newHeaders = new Headers(init?.headers || {})
    newHeaders.set('Authorization', `Bearer ${newToken}`)
    newHeaders.set('apikey', key)
    return rawFetch(input, { ...init, headers: newHeaders })
  } catch (err) {
    console.warn('[supabase] 401 auto-retry failed:', err?.message)
    return res
  }
}

supabase = url && key ? createClient(url, key, {
  global: { fetch: fetchWithTimeout },
  realtime: {
    params: { eventsPerSecond: 10 },
    log_level: 'info',
  },
}) : null

// Expose to window for live diagnostics from devtools console.
// Read-only — gates further behavior on session, so even with this exposed,
// only the signed-in user's RLS-permitted operations work.
if (typeof window !== 'undefined') window.__supabase = supabase

export { supabase }
